// Brand configuration — the single place a deployment's identity lives.
//
// Every value is read from a Vite env var at build time (see .env.example) and
// falls back to a neutral placeholder, so an unconfigured build still renders a
// sensible generic app. Nothing else in the codebase should hardcode an
// organisation name, logo, contact detail or sibling-app URL; import `brand`.

const env = import.meta.env;

/** Read a VITE_* var; empty/whitespace counts as unset. */
function read(name: string, fallback: string): string {
  const v = (env as Record<string, unknown>)[name];
  return typeof v === "string" && v.trim() !== "" ? v.trim() : fallback;
}

/** Like read(), but an explicitly empty value is respected (used to hide things). */
function readAllowEmpty(name: string, fallback: string): string {
  const v = (env as Record<string, unknown>)[name];
  return typeof v === "string" ? v.trim() : fallback;
}

export interface Brand {
  /** Full organisation name, e.g. "Your School". */
  orgName: string;
  /** Short name for tight spaces (header, home-screen label). */
  orgShortName: string;
  /** This app's name, e.g. "Teacher". */
  appName: string;
  /** Logo image URL. Empty → the neutral initial-letter mark (components/BrandMark). */
  logoUrl: string;
  supportEmail: string;
  supportPhone: string;
  /** "Powered by …" credit. Empty string hides it. */
  poweredBy: string;
  /** API base path or absolute URL, without a trailing slash. Default same-origin "/api". */
  apiBase: string;
  /** IANA timezone the school (and the backend) runs in. Dates are pinned to it. */
  timezone: string;
  /** BCP 47 locale for date/number formatting. */
  locale: string;
  /** Links to sibling apps of the suite. Set to an empty string to hide the link. */
  links: {
    ledger: string;
  };
}

export const brand: Brand = {
  orgName: read("VITE_ORG_NAME", "Your School"),
  orgShortName: read("VITE_ORG_SHORT_NAME", "School"),
  appName: read("VITE_APP_NAME", "Teacher"),
  logoUrl: read("VITE_LOGO_URL", ""),
  supportEmail: read("VITE_SUPPORT_EMAIL", "support@example.com"),
  supportPhone: read("VITE_SUPPORT_PHONE", "+00 00000 00000"),
  poweredBy: readAllowEmpty("VITE_POWERED_BY", "Craft&Code"),
  apiBase: read("VITE_API_BASE", "/api").replace(/\/+$/, ""),
  timezone: read("VITE_TIMEZONE", "Asia/Kolkata"),
  locale: read("VITE_LOCALE", "en-IN"),
  links: {
    ledger: readAllowEmpty("VITE_LEDGER_URL", "https://ledger.example.com").replace(/\/+$/, ""),
  },
};

/** "<orgShortName> <appName>", used for titles and notification fallbacks. */
export const fullAppName = `${brand.orgShortName} ${brand.appName}`;

/** Build an API URL from a path that starts with "/". */
export const apiUrl = (path: string): string => `${brand.apiBase}${path}`;
